// Autocomplete - accessible combobox over a text input and a listbox of
// options, positioned by @floating-ui/dom.
//
// Replaces v1 modules/Autocomplete.js, a thin wrapper over the preact-based
// accessible-autocomplete, which built its whole DOM from a JS array. That
// contract was never adopted: no consumer ever instantiated
// window.myApp.Autocomplete - the app wrote its own Stimulus controller over
// stimulus-autocomplete and fed it server-rendered option fragments, reusing
// only .autocomplete__input / __option / __option--focused from the styleguide.
// v2 therefore owns the behavior with no new dependency and takes the shape the
// consumer actually needs. See docs/porting.md for the full reasoning.
//
// HTML contract - options fetched from the server (the real-world case):
//   <div class="autocomplete__wrapper" data-ms-autocomplete
//        data-ms-autocomplete-url="/vyhladavanie/naseptavac">
//     <input class="autocomplete__input" type="text" name="q">
//     <ul class="autocomplete__menu" role="listbox"></ul>
//   </div>
// The endpoint gets ?q=<query> and answers with the options markup:
//   <li class="autocomplete__option" role="option"
//       data-ms-autocomplete-value="123">Šikmý kostol</li>
//
// HTML contract - options written in the page, filtered client-side (no url):
//   <div class="autocomplete__wrapper" data-ms-autocomplete>
//     <input class="autocomplete__input" type="text">
//     <ul class="autocomplete__menu" role="listbox">
//       <li class="autocomplete__option" role="option">Prvá možnosť</li>
//     </ul>
//   </div>
//
// Container attributes: data-ms-autocomplete-url, -param (query param, default
// "q"), -min-length, -delay, -placement, -selected-class, -open-on-focus,
// -submit-on-enter, -no-results, all "true"/"false" for the boolean ones.
// Per option: data-ms-autocomplete-value (committed value), -label (text put in
// the input), -search (extra haystack for local filtering), aria-disabled.

import { attachFloating, clearFloating, type Placement } from './floating';

export type AutocompleteLanguage = 'sk' | 'cz';

/** Committed selection. */
export const AUTOCOMPLETE_CHANGE_EVT = 'martinus:autocomplete:change';
/** Menu opened with options. */
export const AUTOCOMPLETE_OPEN_EVT = 'martinus:autocomplete:open';
/** Menu closed. */
export const AUTOCOMPLETE_CLOSE_EVT = 'martinus:autocomplete:close';
/** Remote source failed. */
export const AUTOCOMPLETE_ERROR_EVT = 'martinus:autocomplete:error';

export interface AutocompleteChangeDetail {
  /** data-ms-autocomplete-value, falling back to the label. */
  value: string;
  /** Text put into the visible input. */
  label: string;
  option: HTMLElement;
}

export interface AutocompleteTexts {
  noResults: string;
  queryTooShort: (minLength: number) => string;
  results: (count: number) => string;
}

export interface AutocompleteConfig {
  /** Characters needed before the source is queried. */
  minLength?: number;
  /** Debounce before the source is queried, ms. */
  delay?: number;
  /** Query parameter name appended to the url. */
  queryParam?: string;
  /** Where the menu opens relative to the field. */
  placement?: Placement;
  /** Gap between field and menu, px. */
  offset?: number;
  /** Class marking the active option, on top of aria-selected. */
  selectedClass?: string;
  /** Query the source on focus, before anything is typed. */
  openOnFocus?: boolean;
  /** Let Enter submit the form after committing an option. */
  submitOnEnter?: boolean;
  /** Show the "no results" row instead of keeping the menu closed. */
  showNoResults?: boolean;
  /** UI language for the status messages; defaults to <html lang>. */
  language?: AutocompleteLanguage;
  /**
   * Custom option source, taking over from data-ms-autocomplete-url. Returns
   * the options markup for a query. Successor of v1's `debounceSource`.
   */
  source?: (query: string, element: HTMLElement) => string | Promise<string>;
  /** Extra fetch() options for the remote source. */
  fetchOptions?: RequestInit;
}

/** Options that can be committed - a disabled row is not one of them. */
const OPTION_SELECTOR = '[role="option"]:not([aria-disabled="true"])';

const ANY_OPTION_SELECTOR = '[role="option"]';

/** Slovak/Czech plural forms: 1, 2-4, 5+. */
const plural = (count: number, forms: [string, string, string]): string => {
  if (count === 1) return forms[0];
  return count < 5 ? forms[1] : forms[2];
};

const texts = {
  sk: {
    noResults: 'Nenašli sme žiadne výsledky.',
    queryTooShort: (minLength: number) => `Minimálny počet znakov je ${minLength}.`,
    results: (count: number) =>
      `${count} ${plural(count, ['výsledok zobrazený', 'výsledky zobrazené', 'výsledkov zobrazených'])}.`,
  },
  cz: {
    noResults: 'Nenašli jsme žádné výsledky.',
    queryTooShort: (minLength: number) => `Minimální počet znaků je ${minLength}.`,
    results: (count: number) =>
      `${count} ${plural(count, ['výsledek zobrazen', 'výsledky zobrazeny', 'výsledků zobrazených'])}.`,
  },
} satisfies Record<AutocompleteLanguage, AutocompleteTexts>;

/** Resolve the default language from <html lang>, as in modules/select.ts. */
const detectLanguage = (): AutocompleteLanguage => {
  const lang = document.documentElement.lang.toLowerCase();
  return lang.startsWith('cs') || lang.startsWith('cz') ? 'cz' : 'sk';
};

const defaultConfig: AutocompleteConfig = {
  minLength: 1,
  delay: 300,
  queryParam: 'q',
  placement: 'bottom-start',
  offset: 2,
  selectedClass: 'autocomplete__option--focused',
  openOnFocus: false,
  submitOnEnter: false,
  showNoResults: true,
};

/** Read a "true"/"false" data attribute, falling back to the config value. */
const boolAttr = (element: HTMLElement, name: string, fallback: boolean): boolean => {
  const value = element.getAttribute(name);
  if (value === null) return fallback;
  // A bare attribute (data-ms-autocomplete-open-on-focus) reads as opt-in.
  return value !== 'false';
};

/** Read a numeric data attribute, falling back to the config value. */
const numAttr = (element: HTMLElement, name: string, fallback: number): number => {
  const value = Number(element.getAttribute(name));
  return Number.isFinite(value) && element.hasAttribute(name) ? value : fallback;
};

/**
 * Fold case and strip diacritics so "safarik" matches "Šafárik" - v1 built a
 * `new RegExp(query, 'i')` instead, which both ignored diacritics differences
 * and threw on a stray "(" in the query.
 */
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');

const normalize = (value: string): string =>
  value.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Parse an options fragment. A <template> keeps bare <li> elements intact. */
const parseOptions = (html: string): DocumentFragment => {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
};

let uid = 0;

let optionUid = 0;

/** One combobox: an input, its menu and the source behind them. */
export class AutocompleteInstance {
  element: HTMLElement;

  input: HTMLInputElement;

  menu: HTMLElement;

  /** Optional companion input carrying the committed value. */
  hidden: HTMLInputElement | null;

  config: AutocompleteConfig;

  texts: AutocompleteTexts;

  private status: HTMLElement;

  /** Options as authored in the page - the pool for local filtering. */
  private pool: DocumentFragment | null = null;

  private timer: ReturnType<typeof setTimeout> | undefined;

  private pending: AbortController | null = null;

  private cleanupFloating: (() => void) | null = null;

  private open = false;

  constructor(element: HTMLElement, input: HTMLInputElement, menu: HTMLElement, config: AutocompleteConfig = {}) {
    this.element = element;
    this.input = input;
    this.menu = menu;
    this.hidden = element.querySelector<HTMLInputElement>(
      '[data-ms-autocomplete-hidden], input[type="hidden"]',
    );

    // Config first, per-element attributes on top of it.
    const merged: AutocompleteConfig = { ...defaultConfig, ...config };

    this.config = {
      ...merged,
      minLength: numAttr(element, 'data-ms-autocomplete-min-length', merged.minLength ?? 0),
      delay: numAttr(element, 'data-ms-autocomplete-delay', merged.delay ?? 0),
      queryParam: element.getAttribute('data-ms-autocomplete-param') ?? merged.queryParam,
      placement:
        (element.getAttribute('data-ms-autocomplete-placement') as Placement | null) ?? merged.placement,
      selectedClass:
        element.getAttribute('data-ms-autocomplete-selected-class') ?? merged.selectedClass,
      openOnFocus: boolAttr(element, 'data-ms-autocomplete-open-on-focus', merged.openOnFocus === true),
      submitOnEnter: boolAttr(
        element,
        'data-ms-autocomplete-submit-on-enter',
        merged.submitOnEnter === true,
      ),
      showNoResults: boolAttr(element, 'data-ms-autocomplete-no-results', merged.showNoResults !== false),
    };

    this.texts = texts[this.config.language ?? detectLanguage()];

    uid += 1;
    if (!this.menu.id) this.menu.id = `ms-autocomplete-menu-${uid}`;
    this.menu.setAttribute('role', 'listbox');

    // ARIA 1.2 combobox: the input owns the expanded state and points at the
    // active option, the menu is the listbox it controls.
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.menu.id);
    this.input.setAttribute('aria-autocomplete', 'list');
    // Keep the browser's own dropdown out of the way of the menu.
    if (!this.input.hasAttribute('autocomplete')) this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('spellcheck', 'false');

    // Only the result count is announced. The active option is left to
    // aria-activedescendant - announcing it here too would double up, the same
    // redundancy modules/gallery.ts avoids by disabling Swiper's a11y layer.
    this.status = document.createElement('div');
    this.status.className = 'autocomplete__status visually-hidden';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.element.append(this.status);

    // Without a source the authored options are the pool. Snapshot them before
    // the first query overwrites the menu.
    if (!this.hasRemoteSource) {
      this.pool = parseOptions(this.menu.innerHTML);
      this.menu.innerHTML = '';
    }

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('keydown', this._onKeydown);
    this.input.addEventListener('blur', this._onBlur);
    if (this.config.openOnFocus) this.input.addEventListener('focus', this._onFocus);
    this.menu.addEventListener('mousedown', this._onMenuMousedown);
    this.menu.addEventListener('click', this._onMenuClick);
  }

  /** Is the menu open? */
  isOpen(): boolean {
    return this.open;
  }

  /** Committable options currently in the menu. */
  get options(): HTMLElement[] {
    return [...this.menu.querySelectorAll<HTMLElement>(OPTION_SELECTOR)];
  }

  /** The option the keyboard is on, if any. */
  get activeOption(): HTMLElement | null {
    return this.menu.querySelector<HTMLElement>('[aria-selected="true"]');
  }

  private get hasRemoteSource(): boolean {
    return Boolean(this.config.source) || this.element.hasAttribute('data-ms-autocomplete-url');
  }

  /**
   * Query the source and render the result. `force` skips the minLength gate -
   * that is how opening on focus shows suggestions for an empty input.
   */
  async search(query = this.input.value.trim(), force = false): Promise<void> {
    clearTimeout(this.timer);

    const minLength = this.config.minLength ?? 0;

    if (!force && query.length < minLength) {
      this._setStatus(query.length > 0 ? this.texts.queryTooShort(minLength) : '');
      this.close();
      return;
    }

    if (!this.hasRemoteSource) {
      this._render(this._filterPool(query));
      return;
    }

    // Drop the in-flight request: without this a slow answer to "ha" could
    // land after the answer to "harry" and overwrite it.
    this.pending?.abort();
    const controller = new AbortController();
    this.pending = controller;
    this.element.setAttribute('data-state', 'loading');

    try {
      const html = this.config.source
        ? await this.config.source(query, this.element)
        : await this._fetchOptions(query, controller.signal);

      if (controller.signal.aborted) return;
      this._render(html);
    } catch (error) {
      if (controller.signal.aborted) return;

      this.close();
      this.element.dispatchEvent(
        new CustomEvent(AUTOCOMPLETE_ERROR_EVT, { bubbles: true, detail: { error, query } }),
      );
    } finally {
      if (this.pending === controller) {
        this.pending = null;
        this.element.removeAttribute('data-state');
      }
    }
  }

  /** Put an option's label in the input and its value in the hidden input. */
  commit(option: HTMLElement): void {
    if (option.getAttribute('aria-disabled') === 'true') return;

    // A linked option navigates - the menu is then just a list of shortcuts.
    if (option instanceof HTMLAnchorElement) {
      this.close();
      option.click();
      return;
    }

    // Without an explicit label the row's text has to do - collapsed, because a
    // row built from several elements is full of markup indentation.
    const label =
      option.getAttribute('data-ms-autocomplete-label')
      ?? option.textContent?.replace(/\s+/g, ' ').trim()
      ?? '';
    const value = option.getAttribute('data-ms-autocomplete-value') ?? label;

    // The visible input always shows the label. stimulus-autocomplete wrote the
    // raw value there when no hidden input was present, which put entity ids in
    // front of the user; the value travels in the event and the hidden input.
    this.input.value = label;

    if (this.hidden) {
      this.hidden.value = value;
      this.hidden.dispatchEvent(new Event('input', { bubbles: true }));
      this.hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close();

    this.element.dispatchEvent(
      new CustomEvent<AutocompleteChangeDetail>(AUTOCOMPLETE_CHANGE_EVT, {
        bubbles: true,
        detail: { value, label, option },
      }),
    );
  }

  /** Clear the input, the hidden value and the menu. */
  clear(): void {
    this.input.value = '';
    if (this.hidden) this.hidden.value = '';
    this.close();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;

    this.menu.classList.remove('autocomplete__menu--visible');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this._deactivate();

    this.cleanupFloating?.();
    this.cleanupFloating = null;
    clearFloating(this.menu);

    this.element.dispatchEvent(new CustomEvent(AUTOCOMPLETE_CLOSE_EVT, { bubbles: true }));
  }

  destroy(): void {
    clearTimeout(this.timer);
    this.pending?.abort();
    this.close();

    this.input.removeEventListener('input', this._onInput);
    this.input.removeEventListener('keydown', this._onKeydown);
    this.input.removeEventListener('blur', this._onBlur);
    this.input.removeEventListener('focus', this._onFocus);
    this.menu.removeEventListener('mousedown', this._onMenuMousedown);
    this.menu.removeEventListener('click', this._onMenuClick);

    ['role', 'aria-expanded', 'aria-controls', 'aria-autocomplete', 'aria-activedescendant'].forEach(
      attribute => this.input.removeAttribute(attribute),
    );
    this.status.remove();
    this.element.removeAttribute('data-state');

    // Hand the authored options back, so a later init finds the same markup.
    if (this.pool) {
      this.menu.innerHTML = '';
      this.menu.append(this.pool.cloneNode(true));
    }
  }

  private _openMenu(): void {
    if (this.open) return;
    this.open = true;

    this.menu.classList.add('autocomplete__menu--visible');
    this.input.setAttribute('aria-expanded', 'true');

    // Anchored on the field, not the input: a search field wraps the input
    // together with its addons, and the menu spans the whole field.
    this.cleanupFloating = attachFloating(this.element, this.menu, {
      placement: this.config.placement,
      offset: this.config.offset,
    });

    this.element.dispatchEvent(new CustomEvent(AUTOCOMPLETE_OPEN_EVT, { bubbles: true }));
  }

  private async _fetchOptions(query: string, signal: AbortSignal): Promise<string> {
    const url = new URL(this.element.getAttribute('data-ms-autocomplete-url') ?? '', window.location.href);
    url.searchParams.set(this.config.queryParam ?? 'q', query);

    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      ...this.config.fetchOptions,
      signal,
    });

    if (!response.ok) {
      throw new Error(`Autocomplete: source responded with ${response.status}`);
    }

    return response.text();
  }

  /** Client-side filtering of the options authored in the page. */
  private _filterPool(query: string): string {
    if (!this.pool) return '';

    const needle = normalize(query);

    return [...this.pool.querySelectorAll<HTMLElement>(ANY_OPTION_SELECTOR)]
      .filter(option => {
        const haystack = [
          // The successor of v1's `raw` field: match against more text than
          // the row shows (an author's whole bio, alternative spellings).
          option.getAttribute('data-ms-autocomplete-search') ?? '',
          option.getAttribute('data-ms-autocomplete-label') ?? '',
          option.textContent ?? '',
        ].join(' ');

        return normalize(haystack).includes(needle);
      })
      .map(option => option.outerHTML)
      .join('');
  }

  private _render(html: string): void {
    this.menu.innerHTML = html.trim();
    this._identifyOptions();

    const { length } = this.options;

    if (length === 0) {
      if (!this.config.showNoResults) {
        this.close();
        return;
      }

      this.menu.innerHTML = `<li class="autocomplete__option autocomplete__option--empty"
        role="option" aria-disabled="true">${escapeHtml(this.texts.noResults)}</li>`;
    }

    this._openMenu();
    this._setStatus(length === 0 ? this.texts.noResults : this.texts.results(length));
  }

  /**
   * aria-activedescendant needs every option to have an id. A running counter
   * rather than the row index, so a generated id can never collide with one the
   * server already put on another option.
   */
  private _identifyOptions(): void {
    this.menu.querySelectorAll<HTMLElement>(`${ANY_OPTION_SELECTOR}:not([id])`).forEach(option => {
      optionUid += 1;
      option.id = `${this.menu.id}-option-${optionUid}`;
    });
  }

  private _activate(option: HTMLElement): void {
    this._deactivate();

    option.setAttribute('aria-selected', 'true');
    if (this.config.selectedClass) option.classList.add(this.config.selectedClass);
    this.input.setAttribute('aria-activedescendant', option.id);
    // Not available in jsdom, and irrelevant for a menu that fits.
    option.scrollIntoView?.({ block: 'nearest' });
  }

  private _deactivate(): void {
    const active = this.activeOption;
    if (!active) return;

    active.removeAttribute('aria-selected');
    if (this.config.selectedClass) active.classList.remove(this.config.selectedClass);
  }

  /** Move the keyboard selection, wrapping around at both ends. */
  private _move(step: 1 | -1): void {
    const { options } = this;
    if (options.length === 0) return;

    const current = this.activeOption;
    const index = current ? options.indexOf(current) : -1;
    const next = options[(index + step + options.length) % options.length];

    if (next) this._activate(next);
  }

  private _setStatus(message: string): void {
    this.status.textContent = message;
  }

  private _onInput = (): void => {
    // Typing invalidates whatever was committed before.
    if (this.hidden) this.hidden.value = '';

    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.search();
    }, this.config.delay);
  };

  private _onFocus = (): void => {
    if (this.open) return;
    void this.search(this.input.value.trim(), true);
  };

  private _onBlur = (): void => {
    this.close();
  };

  private _onKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open && this.options.length > 0) this._openMenu();
        this._move(1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this._move(-1);
        break;

      case 'Enter': {
        const active = this.open ? this.activeOption : null;
        if (!active) return;

        // Committing consumes the Enter, so a combobox inside a form does not
        // submit it by accident. data-ms-autocomplete-submit-on-enter opts into
        // "pick and submit in one keystroke".
        if (!this.config.submitOnEnter) event.preventDefault();
        this.commit(active);
        break;
      }

      case 'Tab':
        // Moving on with an option highlighted takes it, as in v1's autoselect.
        if (this.open && this.activeOption) this.commit(this.activeOption);
        break;

      case 'Escape':
        if (!this.open) return;
        // Do not let a surrounding modal or offcanvas close as well.
        event.stopPropagation();
        event.preventDefault();
        this.close();
        break;

      default:
    }
  };

  // Keep the focus in the input while an option is clicked - otherwise blur
  // closes the menu before the click lands on it.
  private _onMenuMousedown = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private _onMenuClick = (event: MouseEvent): void => {
    const option = (event.target as Element | null)?.closest<HTMLElement>(OPTION_SELECTOR);
    if (!option || !this.menu.contains(option)) return;

    // A linked option navigates on its own, so stay out of the way. This is
    // also what keeps commit()'s option.click() from looping back in here.
    if (option instanceof HTMLAnchorElement) {
      this.close();
      return;
    }

    event.preventDefault();
    this.commit(option);
  };
}

const instances = new WeakMap<HTMLElement, AutocompleteInstance>();

export default class Autocomplete {
  selector: string;

  config: AutocompleteConfig;

  elements: AutocompleteInstance[] = [];

  constructor(selector = '[data-ms-autocomplete]', config: AutocompleteConfig = {}) {
    this.selector = selector;
    this.config = config;

    this.elements = this._initialize();
  }

  /** The combobox belonging to a container element, if it was initialized. */
  static getInstance(element: HTMLElement | null): AutocompleteInstance | null {
    return (element && instances.get(element)) || null;
  }

  /** Re-scan the DOM for comboboxes added since init. */
  update(): void {
    this.destroy();
    this.elements = this._initialize();
  }

  destroy(): void {
    this.elements.forEach(instance => {
      instances.delete(instance.element);
      instance.destroy();
    });
    this.elements = [];
  }

  private _initialize(): AutocompleteInstance[] {
    return [...document.querySelectorAll<HTMLElement>(this.selector)].reduce<AutocompleteInstance[]>(
      (created, element) => {
        const input = element.querySelector<HTMLInputElement>(
          '[data-ms-autocomplete-input], input:not([type="hidden"])',
        );
        const menu = element.querySelector<HTMLElement>(
          '[data-ms-autocomplete-menu], .autocomplete__menu',
        );

        if (!input || !menu) {
          console.error('Autocomplete: container needs an input and a .autocomplete__menu', element);
          return created;
        }

        const instance = new AutocompleteInstance(element, input, menu, this.config);
        instances.set(element, instance);
        created.push(instance);

        return created;
      },
      [],
    );
  }
}
